import { supabase } from './supabase'
import { config } from '../config'
import { withImpersonationHeader } from '../utils/impersonation'

const API_BASE_URL = config.API_BASE_URL

export interface SecretKeyStatus {
  has_secret_key: boolean
  created_at?: string
}

export interface SecretKeyResponse {
  secret_key: string
  message: string
}

class ApiClient {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    if (!supabase) {
      throw new Error('Supabase not configured')
    }

    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('No valid session')
    }

    // The fetch interceptor will automatically add X-Impersonate-User header if needed
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json'
    }
    return withImpersonationHeader(headers)
  }

  private async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const headers = await this.getAuthHeaders()
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers
      }
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`API Error: ${response.status} - ${errorText}`)
    }

    return response.json()
  }

  async checkSecretKeyStatus(): Promise<SecretKeyStatus> {
    return this.makeRequest<SecretKeyStatus>('/auth/secret-key/status')
  }

  async generateSecretKey(): Promise<SecretKeyResponse> {
    return this.makeRequest<SecretKeyResponse>('/auth/secret-key/generate', {
      method: 'POST'
    })
  }

  async revokeSecretKey(): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>('/auth/secret-key/revoke', {
      method: 'DELETE'
    })
  }
}

export const apiClient = new ApiClient() 
